<template>
  <div class="lista-wrapper">
    <div class="lista-table">

      <!-- Table header -->
      <div class="lista-header">
        <div>SKU</div>
        <div>Producto</div>
        <div class="text-right">Unidad</div>
        <div class="text-right">Precio</div>
        <div class="text-center">Cantidad</div>
        <div class="text-right">Subtotal</div>
      </div>

      <!-- Product rows -->
      <div
        v-for="p in products"
        :key="p.id"
        class="lista-row"
        :class="{ 'lista-row--in-order': qty(p.id) > 0 }"
      >
        <div class="lista-row__sku">{{ p.sku }}</div>
        <div class="lista-row__name">{{ p.name }}</div>
        <div class="text-right lista-row__unit">{{ p.unit }}</div>
        <div class="text-right lista-row__price">{{ money(p.price) }}</div>

        <!-- Cantidad: last-order hint + input -->
        <div class="lista-row__qty-cell">
          <span
            v-if="lastQty(p.id)"
            :title="`Último pedido: ${lastQty(p.id)}`"
            class="hint-badge"
          >{{ lastQty(p.id) }}</span>
          <QtyInput
            :modelValue="qty(p.id)"
            size="sm"
            :decimal="p.decimal"
            @update:modelValue="v => $emit('set-qty', { comedorId: focusedComedor, productId: p.id, qty: v })"
          />
        </div>

        <div
          class="text-right lista-row__subtotal"
          :class="{ 'lista-row__subtotal--active': qty(p.id) > 0 }"
        >
          {{ qty(p.id) > 0 ? money(p.price * qty(p.id)) : '—' }}
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { onMounted, watch } from 'vue'
import QtyInput from '../ui/QtyInput.vue'

function money(n) { return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const props = defineProps({
  products:       { type: Array,  required: true },
  focusedComedor: { type: [String, Number], default: null },
  cart:           { type: Object, required: true },
  store:          { type: Object, default: null },
})

defineEmits(['set-qty'])

function qty(productId) {
  return props.cart[props.focusedComedor]?.[productId] || 0
}

function lastQty(productId) {
  return props.store?.lastOrderItems?.[props.focusedComedor]?.[String(productId)] || 0
}

function loadHints() {
  if (props.focusedComedor != null) {
    props.store?.loadLastOrderItems?.([props.focusedComedor])
  }
}

onMounted(loadHints)
watch(() => props.focusedComedor, loadHints)
</script>

<style scoped>
/* ─── Wrapper ────────────────────────────────────────────────────────────────── */
.lista-wrapper {
  padding: 24px;
}

.lista-table {
  background: var(--surface);
  border-radius: 8px;
  overflow-x: auto;
  border: 1px solid var(--border);
  min-width: 0;
}

/* ─── Header ─────────────────────────────────────────────────────────────────── */
.lista-header {
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: 70px 1fr 100px 110px 160px 100px;
  font-family: var(--ff-head);
  font-size: 10px;
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  background: var(--bg);
}

/* ─── Rows ───────────────────────────────────────────────────────────────────── */
.lista-row {
  padding: 10px 18px;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: 70px 1fr 100px 110px 160px 100px;
  align-items: center;
  font-family: var(--ff-body);
  font-size: 13px;
  background: transparent;
  transition: background 0.12s;
}

.lista-row--in-order {
  background: var(--primary-softer);
}

.lista-row__sku {
  color: var(--text-3);
  font-size: 11px;
  font-family: ui-monospace, monospace;
}

.lista-row__name {
  color: var(--text);
  font-weight: 700;
}

.lista-row__unit {
  color: var(--text-2);
}

.lista-row__price {
  color: var(--text);
  font-weight: 700;
}

.lista-row__qty-cell {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Last-order hint in lista mode */
.lista-row__qty-cell .hint-badge {
  position: absolute;
  right: calc(50% + 26px);
  top: 50%;
  transform: translateY(-50%);
}

.lista-row__subtotal {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text-3);
}

.lista-row__subtotal--active {
  color: var(--primary);
}

/* ─── Hint badge (shared) ────────────────────────────────────────────────────── */
.hint-badge {
  font-family: var(--ff-head);
  font-size: 14px;
  font-weight: 700;
  color: var(--hint);
  background: rgba(249, 115, 22, 0.08);
  border: 1.5px solid rgba(249, 115, 22, 0.35);
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
  white-space: nowrap;
}

/* ─── Utility ────────────────────────────────────────────────────────────────── */
.text-right  { text-align: right; }
.text-center { text-align: center; }

/* ─── Hide spinners ──────────────────────────────────────────────────────────── */
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
</style>
